import { Layout, Timeline, Typography } from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";
import { useMemo, type ReactNode } from "react";

import { formatCurrency } from "../../../../../@lib-i18n";
import { Layout as ModalLayout } from "../../../shared/layout";
import type { Page } from "../page";

export function PaymentFlow({
	page,
	defaultLayoutProps,
}: {
	page: Omit<ADT.Pick<Page, "PaymentFlow">, "$type">;
	defaultLayoutProps: ModalLayout.Defaults;
}) {
	const { items, illustration } = useMemo(() => {
		// Completed steps keep the default blue rail dot and carry the tick on
		// the right instead — `status: "success"` would move it onto the rail.
		const done = {
			icon: (
				<Icon.CheckmarkFilled
					width={16}
					height={16}
					className="text-green-1000 dark:text-green-700"
				/>
			),
		};

		// Every step is listed up front so the shape of the flow is visible
		// from the start; `"future"` greys out the ones that have not happened.
		const items: Array<Timeline.Item> = [
			{
				title: "Request Sent",
				// `datetime` is required on non-pending items; an empty string
				// renders the item without a byline.
				datetime: page.createdTimestamp ?? "",
				description: page.handle,
				collapsible: false,
				label: done,
			},
			{
				title: "Viewed",
				datetime: page.viewedTimestamp ?? "",
				...(page.viewedTimestamp
					? { label: done }
					: { status: "future" as const }),
			},
		];

		let approved = false;
		let allDeclined = !!page.transactions.length;
		page.transactions.forEach((transaction) => {
			const isApproved = transaction.status === "approved";
			if (isApproved) {
				// Any "approved" transactions means they can't all be declined.
				approved = true;
				allDeclined = false;
			}

			items.push({
				title: isApproved ? "Payment Approved" : "Payment Declined",
				datetime: "",
				description: isApproved
					? "Successful Transaction"
					: "Please ask your customer to try again.",
				...(isApproved
					? { label: done }
					: {
							status: "error" as const,
						}),
				collapsible: false,
			});
		});

		if (!approved) {
			const isProcessing = page.state.$type === "Processing";

			if (isProcessing) {
				items.push({
					title: "Payment Processing",
					datetime: "",
					collapsible: false,
				});
			} else {
				items.push({
					title: "Payment",
					datetime: "",
					status: "future",
					collapsible: false,
				});
			}
		}

		let illustration: ReactNode = null;
		if (approved) illustration = <Illustration.Ok />;
		else if (allDeclined) illustration = <Illustration.Error />;

		return { items, illustration };
	}, [
		page.transactions,
		page.state,
		page.createdTimestamp,
		page.handle,
		page.viewedTimestamp,
	]);

	return (
		<ModalLayout
			{...defaultLayoutProps}
			{...(page.state.$type === "Approved"
				? {
						actions: [
							{ label: "Done", onClick: page.state.onDone, primary: true },
						],
						onClose: page.state.onDone,
						timeout: { onTimeout: page.state.onDone },
					}
				: {
						// Processing offers no cancel: the payment is already in flight.
						actions:
							page.state.$type === "Processing"
								? undefined
								: [{ label: "Cancel", onClick: page.state.onCancel }],
						onClose:
							page.state.$type === "Processing"
								? undefined
								: page.state.onCancel,
						timeout: !page.cancelling
							? {
									// Delegate the timeout callback to the logic layer.
									onTimeout: true,
									duration: page.state.timeoutDuration,
									visibility: "always",
									label: "Expires in %",
								}
							: undefined,
						pending: page.cancelling ? "close" : undefined,
					})}
		>
			{/* Not vertically centred: growth appends downward instead of
			    re-centring the whole column on each update. */}
			<Layout.Container className="px-32 py-8">
				{/* Plain flex column rather than Layout.Article: its `m-auto`
				    absorbs the free space, which collapses the scrollable list. */}
				<div className="mx-auto flex w-full max-w-[416px] flex-col gap-[26px]">
					{/* Hugs its contents so the gap below is always the 26px the column
					    sets. The slot below reserves the illustration's space on its
					    own, so this block is already a fixed height for a given
					    transaction and nothing moves when the outcome lands. */}
					<div className="flex w-full flex-col items-center">
						{/* The 80px slot the layout reserves. `flex` keeps the SVG out of
						    inline flow, which would otherwise add descender space, and
						    the scale sizes the artwork itself to 80px without changing
						    the box. */}
						<div className="pointer-events-none flex h-[80px] w-[80px] shrink-0 scale-[1.92]">
							{illustration}
						</div>
						<div className="mt-24 flex flex-col items-center gap-12 text-center">
							<Typography variant="heading-4xl">
								{formatCurrency({
									amountInCents: page.total,
									currency: page.currency,
								})}
							</Typography>
							{page.surcharge ? (
								<Typography variant="body-sm" className="text-grey-550">
									{`incl. ${formatCurrency({
										amountInCents: page.surcharge,
										currency: page.currency,
									})} surcharge`}
								</Typography>
							) : null}
						</div>
					</div>
					{/* Only the list scrolls, so a long timeline never pushes the
					    amount or the footer off-screen. The cap is everything the
					    screen spends above it: header, footer, shadows, padding, the
					    180px illustration + amount block, and the gap. */}
					<div className="max-h-[calc(100vh-406px)] overflow-y-auto rounded-8 border border-grey-250 p-16 dark:border-grey-600">
						<Timeline items={items} />
					</div>
				</div>
			</Layout.Container>
		</ModalLayout>
	);
}
